import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';

@Component({
  selector: 'app-custom-combobox',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatIconModule,
    MatButtonModule
  ],
  template: `
    <div class="combobox-container">
      <mat-form-field appearance="fill" class="w-100">
        <mat-label>{{ label }}</mat-label>
        <input #input
               type="text"
               matInput
               [(ngModel)]="searchText"
               [matAutocomplete]="auto"
               (click)="onInputClick()"
               [placeholder]="placeholder"
               [disabled]="disabled"
               [readonly]="!editable"
               [attr.inputmode]="editable ? null : 'none'"
               [attr.autocapitalize]="editable ? null : 'off'"
               [attr.autocorrect]="editable ? null : 'off'"
               [attr.autocomplete]="editable ? 'on' : 'off'">
        <mat-autocomplete #auto="matAutocomplete" (optionSelected)="onSelect($event.option.value)">
          <mat-option *ngFor="let item of filteredItems" [value]="item">
            <div class="option-content">
              <mat-icon *ngIf="showIcon" class="option-icon">{{ iconName }}</mat-icon>
              <div class="option-text">{{ getDisplayText(item) }}</div>
            </div>
          </mat-option>
        </mat-autocomplete>
        <button mat-icon-button matSuffix (click)="clearSelection()" *ngIf="searchText && !disabled" type="button">
          <mat-icon>close</mat-icon>
        </button>
      </mat-form-field>
    </div>
  `,
  styles: [`
    .combobox-container {
      width: 100%;
      min-width: 250px; /* Ensure minimum width */
    }
    
    :host {
      display: block;
      width: 100%;
    }
    
    .mat-mdc-form-field {
      width: 100%;
      display: block;
      
      .mdc-text-field {
        background-color: white !important;
        border: 1px solid #ccc;
        border-radius: 4px;
        padding: 0 !important;
        min-height: 40px;
        width: 100%;
      }

      /* Replace invalid nested selector &__input with a valid selector */
      .mdc-text-field__input {
        padding: 0 12px !important;
        height: 38px !important;
        margin: 0 !important;
      }

      .mdc-text-field:not(.mdc-text-field--disabled) {
        background-color: white !important;
      }
      
      /* Hide any outline/gap pieces to avoid vertical divider */
      .mdc-notched-outline,
      .mdc-notched-outline__leading,
      .mdc-notched-outline__notch,
      .mdc-notched-outline__trailing,
      .mat-mdc-form-field-outline,
      .mat-mdc-form-field-outline-start,
      .mat-mdc-form-field-outline-end,
      .mat-mdc-form-field-outline-gap {
        display: none !important;
      }
      
      .mat-mdc-form-field-flex {
        padding: 0 !important;
        min-height: 40px;
        background: transparent !important;
      }
      
      .mat-mdc-form-field-infix {
        border-top: 0;
        padding: 0 !important;
        min-height: 40px;
        display: flex;
        align-items: center;
        width: 100%;
      }
      
      .mat-mdc-form-field-underline,
      .mat-mdc-form-field-subscript-wrapper,
      .mdc-line-ripple,
      .mat-mdc-form-field-underline::before,
      .mat-mdc-form-field-focus-overlay {
        display: none !important;
      }
      
      input {
        padding: 0 12px !important;
        height: 38px !important;
        margin: 0 !important;
        width: 100%;
      }
      
      .mat-mdc-form-field-wrapper {
        padding: 0 !important;
        margin: 0 !important;
      }
    }
    
    .option-content {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 8px 12px;
      min-height: 40px;
    }
    
    .option-icon {
      color: #666;
      flex-shrink: 0;
    }
    
    .w-100 {
      width: 100%;
    }

    /* Force-remove any MDC outline pieces that can render a vertical divider when label floats */
    :host ::ng-deep .mat-mdc-form-field .mdc-notched-outline,
    :host ::ng-deep .mat-mdc-form-field .mdc-notched-outline__leading,
    :host ::ng-deep .mat-mdc-form-field .mdc-notched-outline__notch,
    :host ::ng-deep .mat-mdc-form-field .mdc-notched-outline__trailing,
    :host ::ng-deep .mat-mdc-form-field .mat-mdc-form-field-outline,
    :host ::ng-deep .mat-mdc-form-field .mat-mdc-form-field-outline-start,
    :host ::ng-deep .mat-mdc-form-field .mat-mdc-form-field-outline-end,
    :host ::ng-deep .mat-mdc-form-field .mat-mdc-form-field-outline-gap,
    :host ::ng-deep .mat-mdc-form-field .mat-mdc-form-field-focus-overlay {
      display: none !important;
      border: 0 !important;
    }

    /* Also neutralize MDC outline custom properties just in case */
    :host ::ng-deep .mat-mdc-form-field .mdc-text-field {
      --mdc-outlined-text-field-outline-color: transparent !important;
      --mdc-outlined-text-field-hover-outline-color: transparent !important;
      --mdc-outlined-text-field-focused-outline-color: transparent !important;
    }
  `]
})
export class CustomComboboxComponent<T> {
  @Input() items: T[] = [];
  @Input() displayField = 'name';
  @Input() placeholder = '';
  @Input() label = '';
  @Input() disabled = false;
  @Input() editable = true; // cuando es false, el input es sólo clickeable (readonly)
  @Input() showIcon = true;
  @Input() iconName = 'category';
  // Permite que el padre fije la selección programáticamente
  private _selectedItem: T | null = null;
  @Input() set selectedItem(value: T | null) {
    this._selectedItem = value;
    // reflejar en el input
    this.searchText = value ? this.getDisplayText(value) : '';
  }
  get selectedItem(): T | null { return this._selectedItem; }
  
  @Output() selectionChange = new EventEmitter<T>();
  
  @ViewChild('input') input: any;
  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger!: MatAutocompleteTrigger;
  
  searchText = '';
  
  get filteredItems(): T[] {
    // En modo no editable, siempre mostramos la lista completa
    if (!this.editable || !this.searchText) {
      return [...this.items];
    }
    const searchLower = this.searchText.toLowerCase();
    return this.items.filter(item => 
      this.getDisplayText(item).toLowerCase().includes(searchLower)
    );
  }
  
  onSelect(item: T): void {
    this.selectedItem = item as any;
    this.searchText = this.getDisplayText(item);
    this.selectionChange.emit(item);
  }
  
  clearSelection(): void {
    this.searchText = '';
    this.selectedItem = null;
    this.selectionChange.emit(undefined as any);
    this.input.nativeElement.focus();
  }
  
  onInputClick(): void {
    this.autocompleteTrigger.openPanel();
  }
  
  getDisplayText(item: T): string {
    return item ? (item as any)[this.displayField] || '' : '';
  }
}
